import React from 'react';
import classNames from 'classnames';
import { useId } from '@reach/auto-id';
// framer-motion v7+ requires React 18+ (we're on 17) so the options here VS their docs might be off until that can happen
import { motion, AnimatePresence } from 'framer-motion';
import I18n from 'i18n-js';
import {
  useFloating,
  useInteractions,
  FloatingFocusManager,
  FloatingPortal,
  useDismiss,
} from '@floating-ui/react';

import { TOAST_DOM_ID } from '../../constants';
import { RCCloseButton, RCIcon } from '../';
import HeartFilledIcon from '../../icons/core/heart-filled.svg?component';
import HeartEmptyIcon from '../../icons/core/heart-empty.svg?component';
import CheckCircleIcon from '../../icons/core/check-circle.svg?component';

type ToastThemes = 'plain' | 'watch-add' | 'watch-remove' | 'success';

interface RCToastProps {
  /**
   * Controls if the Toast should display.
   *
   * Keep in mind that Toast will self-close after 10 seconds unless the user is hovering over it.
   * */
  isOpen: boolean,

  /**
   * This is auto-fired after 10 seconds unless the user is hovering over it.
  */
  onClose: Function,

  /**
   * The toast message. Keep it short (1-4 words) to minimize text wrapping on mobile, especially for non-English languages.
  */
  text: string,

  /**
   * Our themes dictate the color & icon of a given toast;
   * if you need a new theme, the Cadence team can help (or simply sign off on the new theme you make).
  */
  theme?: ToastThemes,

  /** A CoreLink element without any styles. Default link text should use 'commons.RCToast.linkDefault' ("View").
   * Custom text should be very short (1-2 words max) for the best UI/UX balance.
   */
  link?: React.AnchorHTMLAttributes<HTMLAnchorElement>,

  /** Value in rems greater than 1. Adjust toast placement to avoid covering important UI elements. */
  mobileOffset?: number,

  /** How long should the toast stay on the screen? This value only applies to toasts _without_ a `link` present.
   * If a `link` is present, we have a fixed delay to help users "find" that link in a consistent amount of time.
   * Short = 2.5 seconds, medium = 5 seconds (default), long = 10 seconds (default when link is present)
   */
  delayInterval?: 'short' | 'medium' | 'long';
}

const DELAY_TIME_MS = {
  short: 2000,
  medium: 5000,
  long: 10000,
};

export function RCToast({
  text,
  isOpen,
  onClose,
  theme = 'plain',
  link = undefined,
  mobileOffset = undefined,
  delayInterval = 'medium',
}: RCToastProps) {
  const [hasAttention, setHasAttention] = React.useState(false);
  const [hasInteracted, setHasInteracted] = React.useState(false);
  const delayDuration = link ? DELAY_TIME_MS.long : DELAY_TIME_MS[delayInterval];
  const BUFFERED_CLOSE_DELAY = 1000;
  const id = useId();
  const open = isOpen; // Needed to avoid Window open() function collision
  const {
    context,
    floating,
  } = useFloating({
    open,
    onOpenChange: handleEscPress,
  });

  const { getFloatingProps } = useInteractions([
    useDismiss(context, {
      enabled: true,
      escapeKey: true,
      outsidePress: false,
    }),
  ]);

  React.useEffect(() => {
    let timer;

    if (!isOpen) return;

    if (hasAttention) {
      clearTimeout(timer);
    }

    if (!hasInteracted) {
      timer = setTimeout(() => handleClose(), delayDuration);
    }

    if (hasInteracted) {
      timer = setTimeout(() => handleClose(), BUFFERED_CLOSE_DELAY);
    }

    return () => clearTimeout(timer);
  }, [isOpen, hasAttention, hasInteracted]);

  function handleEscPress() {
    setHasAttention(false);
    setHasInteracted(false);
    onClose();
  }

  function handleClose() {
    if (hasAttention) return;
    setHasInteracted(false);
    onClose();
  }

  function onAttentionLeave() {
    setHasAttention(false);
  }

  function onHasAttention() {
    if (hasAttention) return;
    setHasAttention(true);
    setHasInteracted(true);
  }

  function isDialog() {
    return !!link;
  }

  const themeIcon = {
    'plain': null,
    'watch-add': HeartFilledIcon,
    'watch-remove': HeartEmptyIcon,
    'success': CheckCircleIcon,
  };

  function toastIcon() {
    const icon = themeIcon[theme];
    if (!icon) return null;

    const iconClasses = classNames(
      'rc-toast__icon', {
        'rc-toast__icon--watch-add': theme === 'watch-add',
        'rc-toast__icon--watch-remove': theme === 'watch-remove',
        'rc-toast__icon--success': theme === 'success',
      },
    );

    return (
      <div className={iconClasses}>
        <RCIcon inline svgComponent={icon} />
      </div>
    );
  }

  const mobileOffsetStyle = { '--mobile-offset': `${mobileOffset}rem` } as React.CSSProperties;

  function innerContents() {
    return (
      <div
        className="rc-toast-wrapper"
        style={mobileOffset ? mobileOffsetStyle : {}}
      >
        <motion.div
          ref={floating}
          key="toast"
          className="rc-toast"
          initial={{ opacity: 0, y: '2rem' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '2rem', transition: { duration: 0.3 } }}
          onMouseOver={() => onHasAttention()}
          onMouseLeave={() => onAttentionLeave()}
          role={isDialog() ? 'dialog' : 'status'}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getFloatingProps()}
        >
          <div className="rc-toast__content">
            {toastIcon()}
            <div className="rc-toast__text" id={id}>
              {text}
            </div>
          </div>
          {isDialog() && link}
          <div className="rc-toast__close-button">
            <RCCloseButton
              onClick={() => onClose()}
              inverted
              aria-label={I18n.t('cadence.RCToast.closeDialog')}
            />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <FloatingPortal id={TOAST_DOM_ID}>
      <AnimatePresence>
        {isOpen && (
          <>
            {isDialog() ? (
              <FloatingFocusManager
                context={context}
                order={['content']}
                returnFocus
                modal
              >
                {innerContents()}
              </FloatingFocusManager>
            ) : (
              <>
                {innerContents()}
              </>
            )}
          </>
        )}
      </AnimatePresence>
    </FloatingPortal>
  );
}
